<template>
  <div
    class="relative min-h-screen w-screen overflow-hidden bg-white lg:grid lg:h-screen lg:grid-cols-[46%_54%]"
  >
    <main
      class="flex h-full w-full overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      role="main"
    >
      <div class="mx-auto flex min-h-full w-full max-w-[620px] flex-col px-6 sm:px-10 lg:px-16">
        <div class="pt-8 sm:pt-10 lg:pt-12">
          <img :src="evidenceLogo" alt="Evidence" class="h-8 w-auto" />
        </div>

        <div class="flex flex-1 items-center py-10 sm:py-14 lg:py-10">
          <form class="w-full max-w-[420px] space-y-8" @submit.prevent="login">
            <div class="space-y-3">
              <h1
                class="text-3xl font-semibold leading-tight text-gray-900 lg:text-[36px] lg:leading-10"
              >
                Welcome Back
              </h1>
              <p class="text-lg leading-7 text-gray-500 lg:text-xl">
                Please login using your account credentials.
              </p>
            </div>

            <div class="space-y-5">
              <TextInput
                id="email"
                ref="emailInput"
                v-model="email"
                label="Email"
                type="email"
                placeholder="Example@email.com"
                :rules="[
                  (v) => !!v || 'Email is required',
                  (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
                ]"
                @keyup.enter="login"
              />
              <TextInput
                id="password"
                ref="passwordInput"
                v-model="password"
                label="Password"
                type="password"
                placeholder="At least 8 characters"
                :rules="[(v) => !!v || 'Password is required']"
                @keyup.enter="login"
              />
              <div class="flex justify-end">
                <RouterLink
                  to="/forgot-password"
                  class="text-sm font-medium text-[#2748B5] hover:underline focus:outline-hidden focus-visible:underline"
                >
                  Forgot Your Password?
                </RouterLink>
              </div>
            </div>

            <button
              type="submit"
              class="w-full rounded-lg bg-[#2748B5] py-3 text-lg font-medium text-center text-white transition hover:bg-[#1c3aa9] disabled:opacity-50"
              :disabled="loading.isLoading.value"
              :aria-busy="loading.isLoading.value"
            >
              Sign in
            </button>

            <div class="flex items-center justify-center gap-2 border-t pt-8">
              <span class="text-xs tracking-widest text-[#9ca3af] uppercase">DEVELOPED BY</span>
              <img :src="betterDevicesLogo" class="h-6" alt="BetterDevices" />
            </div>
          </form>
        </div>

        <footer class="w-full max-w-[420px] pb-6 sm:pb-8">
          <div
            class="flex flex-col gap-2 text-xs text-[#6a7282] sm:flex-row sm:items-center sm:justify-between sm:text-sm"
          >
            <span>Copyright © {{ currentYear }} 4BetterDevices</span>
            <a class="cursor-pointer hover:underline">Privacy Policy</a>
          </div>
        </footer>
      </div>
    </main>

    <RightPanel />
  </div>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import TextInput from "@app/components/Global/Inputs/TextInput.vue";
import RightPanel from "@app/components/Global/RightPanel.vue";
import { UsersService } from "@core/application/users.service";
import useSnackbar from "@app/composables/use-snackbar";
import { SnackbarState } from "@app/types";
import { injectStrict } from "@app/utils/injectStrict";
import { AuthKey, UsersServiceKey } from "@app/injectionKeys";
import useLoading from "@app/composables/use-loading";
import useWebExtension from "@app/composables/useWebExtension";
import evidenceLogo from "@app/assets/images/evidence-logo.png";
import betterDevicesLogo from "@app/assets/images/4betterdevices-logo.png";

const usersService: UsersService = injectStrict(UsersServiceKey);
const email = ref<string>("");
const password = ref<string>("");
const snackbar = useSnackbar();
const loading = useLoading();

const emailInput = ref<InstanceType<typeof TextInput>>();
const passwordInput = ref<InstanceType<typeof TextInput>>();
const currentYear = new Date().getFullYear();

const { loginExtension } = useWebExtension();
const { getRecoveryLink } = injectStrict(AuthKey);

async function login() {
  const isEmailValid = emailInput.value?.validate();
  const isPasswordValid = passwordInput.value?.validate();
  if (!isEmailValid || !isPasswordValid) return;
  loading.start();
  try {
    await usersService.login(email.value.trim(), password.value.trim());
    const recoveryLink = await getRecoveryLink();
    await loginExtension(recoveryLink).catch(() => null);
    window.location.reload();
  } catch (e: any) {
    loading.stop();
    const kratosError = e?.response?.data?.ui?.messages?.find((m: any) => m.type === "error")?.text;
    snackbar.show(SnackbarState.ERROR, kratosError || "Login failed");
    throw e;
  }
}
</script>
